import { useState } from 'react';
import { api } from '@/lib/api.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { fmtCents } from '@/components/money/format.js';

// R35 (NIGHT-13, David 2026-11-02): "A payment linked to a statement settles the confirmed lines it covers." For each
// payment linked to this statement, the lines the server worked out it paid, and one button that allocates it to them.
// Not ready says why in words (a line to confirm first, two sets that fit, none that fits). The server derives the set
// again when the button is pressed and refuses one that moved. This screen derives nothing.

function PaymentRow({ p, statementId, onDone }) {
  const [busy, setBusy] = useState(false);
  const here = p.links.find((k) => k.statement_id === statementId);
  const lineIds = p.links.flatMap((k) => k.lines.map((l) => l.id));
  const settle = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/money/carrier-payments/${p.payment_id}/settle`, { line_ids: lineIds });
      const still = (r.remitted || []).filter((x) => x.status !== 'succeeded').length;
      toast.success(`${fmtCents(p.amount)} of ${p.paid_on} settled against ${lineIds.length} line${lineIds.length === 1 ? '' : 's'}.${still ? ` ${still} deal${still === 1 ? '' : 's'} still owe${still === 1 ? 's' : ''} more than was paid.` : ''}${(r.fronts || []).length ? ` ${(r.fronts || []).length} fronted for a client who had not paid.` : ''}`);
      await onDone();
    } catch (e) {
      toast.error(e.body?.detail || e.body?.code || e.message || 'Could not settle the payment');
    } finally { setBusy(false); }
  };
  if (p.settled) return <li className="py-1.5 text-xs text-text-dim">{p.paid_on} · {fmtCents(p.amount)}{p.reference ? ` · ${p.reference}` : ''}: settled.</li>;
  return (
    <li className="py-2 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-text">Paid {p.paid_on} · {fmtCents(p.amount)}</span>
        {p.reference ? <span className="text-xs text-text-muted">{p.reference}</span> : null}
        {p.links.length > 1 ? <span className="text-xs text-text-muted">({fmtCents(here ? here.amount : 0)} of it on this statement)</span> : null}
      </div>
      {p.links.map((k) => (
        <div key={k.statement_id} className="mt-1 text-xs text-text-muted">
          {p.links.length > 1 ? <span className="text-text">{k.statement}, {fmtCents(k.amount)}: </span> : null}
          {k.lines.length
            ? k.lines.map((l) => `${l.insured_text} ${fmtCents(l.net_due)}${l.confirmed ? '' : ' (not confirmed)'}`).join(' · ')
            : 'no lines found'}
        </div>
      ))}
      {p.ready
        ? <Button size="sm" className="mt-2" onClick={settle} disabled={busy}>{busy ? 'Settling...' : `Settle: allocate ${fmtCents(p.amount)} to ${lineIds.length} line${lineIds.length === 1 ? '' : 's'}`}</Button>
        : <div className="mt-1 text-xs text-danger">Not settled yet: {p.why}.</div>}
    </li>
  );
}

export function PaymentSettlements({ payments, statementId, onDone }) {
  const mine = (payments || []).filter((p) => p.links.some((k) => k.statement_id === statementId));
  if (!mine.length) return null;
  return (
    <div className="mt-2 rounded-card border border-border bg-bg p-3">
      <div className="text-xs text-text-muted">Payments linked to this statement, and the lines each paid</div>
      <ul className="mt-1 divide-y divide-border">
        {mine.map((p) => <PaymentRow key={p.payment_id} p={p} statementId={statementId} onDone={onDone} />)}
      </ul>
    </div>
  );
}

// R33: a statement whose due date was not read. The broker types the date the statement states.
export function DueDateEntry({ statementId, onDone }) {
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/money/statements/${statementId}/due-date`, { due_date: due });
      toast.success(`Due ${r.due_date}, as the statement states.`);
      setDue('');
      await onDone();
    } catch (e) {
      toast.error(e.body?.detail || e.body?.code || e.message || 'Could not save the due date');
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-1 flex flex-wrap items-end gap-2 text-xs text-text-muted">
      <span className="text-danger">No due date on the statement.</span>
      <label className="text-xs text-text-muted">If it states one, type it
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1 h-9" />
      </label>
      <Button size="sm" variant="outline" onClick={save} disabled={busy || !due}>{busy ? 'Saving...' : 'Save due date'}</Button>
    </div>
  );
}
